/**
 * Client for the granular-permissions API (auth-service /auth/permissions/*)
 * plus the small helpers the permission-builder needs (user list, resource
 * instance loading). Mirrors the fetch + authedHeaders convention used by
 * authStore rather than the axios clients, since these all hit auth-service.
 */
import { authedHeaders } from '../store/authStore';
import type { CatalogModule, EffectivePermissions, Grants } from '../lib/permissionCatalog';

const AUTH_API = import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:8011';
const PERM = `${AUTH_API}/auth/permissions`;

// Service base URLs for lazy-loading resource instances ("selected" mode).
// Keys match the `service` field in the permission catalog.
// apps-service is served at the domain ROOT in production (its
// VITE_APPS_SERVICE_URL is intentionally left unset), so it must fall back to
// window.location.origin — NOT a hardcoded localhost port, which broke the
// installed-apps picker in prod (requested http://localhost:8028/app-installs).
// Mirrors the canonical resolution in src/modules/external_apps/api.ts.
const APPS_BASE: string =
  (import.meta.env.VITE_APPS_SERVICE_URL as string) ||
  (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'http://localhost:8028');

const SERVICE_BASE: Record<string, string> = {
  ontology: import.meta.env.VITE_ONTOLOGY_SERVICE_URL || 'http://localhost:8004',
  connector: import.meta.env.VITE_CONNECTOR_SERVICE_URL || 'http://localhost:8001',
  pipeline: import.meta.env.VITE_PIPELINE_SERVICE_URL || 'http://localhost:8002',
  logic: import.meta.env.VITE_LOGIC_SERVICE_URL || 'http://localhost:8012',
  agent: import.meta.env.VITE_AGENT_SERVICE_URL || 'http://localhost:8013',
  apps: APPS_BASE,
  eval: import.meta.env.VITE_EVAL_SERVICE_URL || 'http://localhost:8016',
  discovery: import.meta.env.VITE_DISCOVERY_SERVICE_URL || 'http://localhost:8030',
};

async function j<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ── Catalog ──────────────────────────────────────────────────────────────────
export interface CatalogPayload {
  catalog: CatalogModule[];
  capabilities: Record<string, string>;
}

export async function fetchCatalog(): Promise<CatalogPayload> {
  return j(await fetch(`${PERM}/catalog`, { headers: authedHeaders() }));
}

// ── Profiles ─────────────────────────────────────────────────────────────────
export interface PermissionProfile {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  grants: Grants;
  is_system: boolean;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  user_count?: number;
}

export async function listProfiles(): Promise<PermissionProfile[]> {
  const data = await j<{ profiles: PermissionProfile[] }>(
    await fetch(`${PERM}/profiles`, { headers: authedHeaders() }),
  );
  return data.profiles;
}

export async function createProfile(body: {
  name: string; description?: string; grants: Grants;
}): Promise<PermissionProfile> {
  return j(await fetch(`${PERM}/profiles`, {
    method: 'POST',
    headers: authedHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }));
}

export async function updateProfile(id: string, body: {
  name?: string; description?: string; grants?: Grants;
}): Promise<PermissionProfile> {
  return j(await fetch(`${PERM}/profiles/${id}`, {
    method: 'PUT',
    headers: authedHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }));
}

export async function deleteProfile(id: string): Promise<void> {
  const res = await fetch(`${PERM}/profiles/${id}`, {
    method: 'DELETE',
    headers: authedHeaders(),
  });
  if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
}

// ── User assignments + effective ─────────────────────────────────────────────
export interface UserAssignments {
  profile_ids: string[];
  override: Grants | null;
}

export async function getAssignments(userId: string): Promise<UserAssignments> {
  return j(await fetch(`${PERM}/users/${userId}/assignments`, { headers: authedHeaders() }));
}

export async function setAssignments(
  userId: string, body: { profile_ids: string[]; override?: Grants | null },
): Promise<UserAssignments> {
  return j(await fetch(`${PERM}/users/${userId}/assignments`, {
    method: 'PUT',
    headers: authedHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  }));
}

export async function getUserEffective(userId: string): Promise<EffectivePermissions> {
  return j(await fetch(`${PERM}/users/${userId}/effective`, { headers: authedHeaders() }));
}

export async function getMyEffective(): Promise<EffectivePermissions> {
  return j(await fetch(`${PERM}/me`, { headers: authedHeaders() }));
}

/** INTRO-17: the caller's own `perm_version`, a bare integer. Polled by
 *  `lib/permSync` to learn that an admin changed what the person may see. */
export async function getMyPermVersion(): Promise<number> {
  const body = await j<{ perm_version?: number }>(
    await fetch(`${PERM}/me/version`, { headers: authedHeaders(), cache: 'no-store' }),
  );
  return Number(body.perm_version ?? 0);
}

// ── Users (reuse auth-service user CRUD) ─────────────────────────────────────
export interface AuthUserRow {
  id: string; email: string; name: string; role: string; is_active: boolean;
}

export async function listUsers(): Promise<AuthUserRow[]> {
  const data = await j<{ users: AuthUserRow[] }>(
    await fetch(`${AUTH_API}/auth/users`, { headers: authedHeaders() }),
  );
  return data.users;
}

// ── Resource instances (for "selected" mode pickers) ─────────────────────────
export interface ResourceInstance { id: string; label: string; }

/**
 * Lazy-load the instances of a resource type so an admin can pick specific
 * ones. Tolerant of the varied list-response shapes across services (array,
 * {items}, {object_types}, …) and fails soft — the builder falls back to
 * All/None when this throws.
 */
export async function loadResourceInstances(
  service: string | null, listEndpoint: string | null, idField: string, labelField: string,
): Promise<ResourceInstance[]> {
  if (!service || !listEndpoint) return [];
  const base = SERVICE_BASE[service];
  if (!base) return [];
  const res = await fetch(`${base}${listEndpoint}`, { headers: authedHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  let rows: any[] = [];
  if (Array.isArray(data)) rows = data;
  else if (data && typeof data === 'object') {
    const arr = Object.values(data).find((v) => Array.isArray(v));
    rows = (arr as any[]) || [];
  }
  // App installs carry only app_id (a slug), not the manifest's display name.
  // Join against the registry so the picker shows real names ("Slack Messenger"
  // rather than "slack-messenger" or a UUID). Best-effort — falls back to the
  // slug if the registry fetch fails.
  let appNames: Record<string, string> | null = null;
  if (service === 'apps') {
    try {
      const reg = await fetch(`${base}/app-registry/apps`, { headers: authedHeaders() });
      if (reg.ok) {
        const catalog = await reg.json();
        const list: any[] = Array.isArray(catalog)
          ? catalog
          : (Object.values(catalog).find((v) => Array.isArray(v)) as any[]) || [];
        appNames = {};
        for (const a of list) {
          if (a?.app_id) appNames[a.app_id] = a.display_name ?? a.name ?? a.app_id;
        }
      }
    } catch {
      // ignore — fall back to slug/id labels
    }
  }

  return rows.map((r) => {
    const id = r?.[idField] ?? r?.id ?? r?.install_id ?? '';
    // Resolve a human label robustly across the varied service shapes
    // (snake/camel, app slug, etc.); fall back to the id only as a last resort.
    const label =
      (appNames && r?.app_id && appNames[r.app_id]) ??
      r?.[labelField] ?? r?.display_name ?? r?.displayName ?? r?.name ??
      r?.app_id ?? r?.label ?? r?.title ?? id;
    return { id: String(id), label: String(label) };
  }).filter((r) => r.id);
}

/**
 * Load the field/property names of an ontology object type (for the Phase 3
 * field-restriction picker). Fails soft to [].
 */
export async function loadObjectTypeFields(objectTypeId: string): Promise<{ name: string; label: string }[]> {
  const base = SERVICE_BASE.ontology;
  const res = await fetch(`${base}/object-types/${objectTypeId}`, { headers: authedHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const props = (data?.properties || data?.data?.properties || []) as any[];
  return props
    .map((p) => ({ name: String(p?.name ?? ''), label: String(p?.display_name ?? p?.displayName ?? p?.name ?? '') }))
    .filter((p) => p.name);
}
